import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAxios from "../../../hooks/useAxios";
import "./StudentsManagementPage.css";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Button,
} from "@mui/material";
import Header from "../../../components/Header/Header";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import StudentsTable from "../../../components/DashboardComponents/StudentsTable/StudentsTable";
import CreateStudentModal from "../../../components/DashboardComponents/Modals/CreateStudentModal/CreateStudentModal";
import ImportStudentsModal from "../../../components/DashboardComponents/Modals/ImportStudentsModal/ImportStudentsModal";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const navLinks = [];

function StudentsManagementPage() {
  const axiosInstance = useAxios();
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get('/student-accounts/created-students');
      
      if (response.data.success) {
        setStudents(response.data.data.students);
      } else {
        setError(response.data.message || 'Failed to fetch students');
        toast.error(response.data.message || "Failed to fetch students.");
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while fetching students');
      toast.error(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleCreateStudent = () => {
    setIsCreateModalOpen(true);
  };

  const handleImportStudents = () => {
    setIsImportModalOpen(true);
  };

  const handleCloseCreateModal = (shouldRefresh = false) => {
    setIsCreateModalOpen(false);
    if (shouldRefresh) {
      fetchStudents();
    }
  };

  const handleCloseImportModal = (shouldRefresh = false) => {
    setIsImportModalOpen(false);
    if (shouldRefresh) {
      fetchStudents();
    }
  };

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
        <Typography variant="h6" className="loading-text">
          Fetching Students...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="error-container">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button 
          onClick={fetchStudents} 
          className="primary-button mt-3"
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="students-management-container page-container">
        {/* <ContentHeader 
          title="Student Management" 
          description="Create and manage student accounts for your classes or family."
        /> */}

        {/* Action Buttons */}
        <Box className="action-buttons-container">
          <Button
            variant="contained"
            className="action-button primary-button"
            startIcon={<PersonAddIcon />}
            onClick={handleCreateStudent}
            size="large"
          >
            Create Student
          </Button>
          <Button
            variant="contained"
            className="action-button secondary-button"
            startIcon={<UploadFileIcon />}
            onClick={handleImportStudents}
            size="large"
          >
            Import Students
          </Button>
        </Box>

        {/* Students Table Section */}
        <ContentHeader title="Your Students" />
        <StudentsTable 
          students={students} 
          onRefresh={fetchStudents}
        />
        
        {/* Modals */}
        <CreateStudentModal
          open={isCreateModalOpen}
          onClose={handleCloseCreateModal}
        />
        
        <ImportStudentsModal
          open={isImportModalOpen}
          onClose={handleCloseImportModal}
        />
      </Container>
      
      <ToastContainer position="top-right" autoClose={5000} />
    </>
  );
}

export default StudentsManagementPage; 